import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  FiCheckCircle,
  FiClock,
  FiInfo,
  FiPhone,
  FiRefreshCw,
  FiSave,
  FiShield,
  FiUser,
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { AppDispatch } from "../../store";
import ECSLoader from "../../components/common/EcsLoader";
import {
  fetchWhatsAppCategories,
  fetchWhatsAppProfile,
} from "../../features/meta-profile-controller/MetaProfile";
import { MetaProfileData, WhatsAppCategory } from "../../types/MetaProfileType";
import { BusinessProfileModal } from "../../components/modal/MetaProfileModal";

export default function WhatsAppProfileView() {
  const dispatch = useDispatch<AppDispatch>();
  const [showBusinessForm, setShowBusinessForm] = useState(false);
  const [profileData, setProfileData] = useState<MetaProfileData | null>(null);
  const [categories, setCategories] = useState<WhatsAppCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [newDisplayName, setNewDisplayName] = useState("");
  const [category, setCategory] = useState("OTHER");
  const [about, setAbout] = useState("Hey there! I am using WhatsApp.");
  const [description, setDescription] = useState("");
  const [address, setAddress] = useState("");
  const [email, setEmail] = useState("");
  const [website1, setWebsite1] = useState("");
  const [website2, setWebsite2] = useState("");

  useEffect(() => {
    loadData();
  }, [dispatch]);

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const [profileRes, categoriesRes] = await Promise.all([
        dispatch(fetchWhatsAppProfile()).unwrap(),
        dispatch(fetchWhatsAppCategories()).unwrap(),
      ]);

      setProfileData(profileRes);
      setCategories(categoriesRes || []);

      if (profileRes?.profile) {
        const prof = profileRes.profile;
        setCategory(prof.vertical || "OTHER");
        setAbout(prof.about || "Hey there! I am using WhatsApp.");
        setDescription(prof.description || "");
        setAddress(prof.address || "");
        setEmail(prof.email || "");
        setWebsite1(prof.websites?.[0] || "");
        setWebsite2(prof.websites?.[1] || "");
      }
    } catch (err: any) {
      setError(err?.message || err || "Failed to load WhatsApp Profile");
    } finally {
      setLoading(false);
    }
  };

  const { account, phone, phoneNumbers = [], profile } = profileData || {};
  const categoryLabel =
    categories.find((c) => c.code === category)?.label ||
    profile?.verticalLabel ||
    "Other";

  return (
    <section className="min-h-screen ">
      <ECSLoader show={loading} />

      {!loading && (
        <div className="mx-auto max-w-7xl space-y-6 pb-12">
          {/* Error Banner */}
          {error && (
            <div className="flex items-center justify-between rounded-xl border border-red-100 bg-red-50 p-4 text-xs font-medium text-red-600">
              <div className="flex items-center gap-2">
                <FiInfo className="h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
              <button
                type="button"
                onClick={loadData}
                className="flex items-center gap-1.5 font-semibold text-red-700 hover:underline"
              >
                <FiRefreshCw /> Retry
              </button>
            </div>
          )}

          <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="relative h-32 bg-[radial-gradient(120%_140%_at_0%_0%,#0288D1_0%,#014B73_45%,#0E1C2B_100%)] p-4">
              <div className="flex justify-between items-start">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur-md">
                  <FaWhatsapp className="text-emerald-300" /> WhatsApp Business
                </span>
                <button
                  type="button"
                  onClick={() => setShowBusinessForm(true)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-1.5 text-xs font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 active:scale-95"
                >
                  <FiSave className="h-3.5 w-3.5 text-emerald-600" /> Edit Profile
                </button>
              </div>
            </div>

            <div className="px-6 pb-6">
              <div className="-mt-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div className="flex items-end gap-4">
                  <div className="relative shrink-0">
                    <div className="h-24 w-24 overflow-hidden rounded-2xl border-4 border-white bg-gray-100 shadow-md">
                      {profile?.profilePictureUrl ? (
                        <img
                          src={profile.profilePictureUrl}
                          alt="Profile"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-gray-400">
                          <FiUser className="h-10 w-10" />
                        </div>
                      )}
                    </div>
                    {account?.businessVerificationStatus === "verified" && (
                      <div className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-emerald-500 text-white">
                        <FiCheckCircle className="h-3.5 w-3.5" />
                      </div>
                    )}
                  </div>

                  <div className="pb-1">
                    <div className="flex items-center gap-2">
                      <h1 className="text-xl font-bold tracking-tight text-gray-900">
                        {phone?.verifiedName || account?.name || "Account"}
                      </h1>
                      {phone?.nameStatus === "APPROVED" && (
                        <StatusBadge text="Approved" variant="success" />
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1 font-medium text-gray-700">
                        <FiPhone className="h-3.5 w-3.5 text-gray-400" />
                        {phone?.displayPhoneNumber || "-"}
                      </span>
                      <span>•</span>
                      <span>{categoryLabel}</span>
                    </div>
                  </div>
                </div>

                <StatusBadge
                  text={phone?.status || "UNKNOWN"}
                  variant={phone?.status === "CONNECTED" ? "success" : "neutral"}
                />
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard
              icon={<FiShield />}
              label="Business"
              value={account?.businessVerificationStatus === "verified" ? "Verified" : "Not verified"}
              active={account?.businessVerificationStatus === "verified"}
            />
            <StatCard
              icon={<FiCheckCircle />}
              label="Account Review"
              value={account?.accountReviewStatus || "-"}
              active={account?.accountReviewStatus === "APPROVED"}
            />
            <StatCard
              icon={<FiShield />}
              label="Quality Rating"
              value={phone?.qualityLabel || phone?.qualityRating || "-"}
              active={phone?.qualityRating === "GREEN"}
            />
            <StatCard
              icon={<FiClock />}
              label="Throughput"
              value={phone?.throughput || "-"}
            />
          </div>

          {/* Account Overview */}
          <Card icon={<FiShield />} title="Account Overview" subtitle="Meta Business & WhatsApp details">
            <div className="grid grid-cols-2 gap-6 p-6 sm:grid-cols-4">
              <InfoItem label="Account ID" value={account?.id} />
              <InfoItem label="Business Name" value={account?.ownerBusinessName} />
              <InfoItem label="Currency" value={account?.currency} />
              <InfoItem label="Timezone" value={account?.timezoneId ? `TZ ${account.timezoneId}` : "-"} />
            </div>
          </Card>

          {/* Phone Numbers Table */}
          <Card
            icon={<FiPhone />}
            title="Connected Numbers"
            subtitle={`${phoneNumbers.length} connected number${phoneNumbers.length !== 1 ? "s" : ""}`}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="border-y border-gray-100 bg-gray-50/50 uppercase tracking-wider text-gray-400 font-semibold">
                  <tr>
                    <th className="px-6 py-3">Phone Number</th>
                    <th className="px-6 py-3">Display Name</th>
                    <th className="px-6 py-3">Name Status</th>
                    <th className="px-6 py-3">Connection</th>
                    <th className="px-6 py-3">Quality</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-gray-700">
                  {phoneNumbers.map((num) => (
                    <tr key={num.id} className="hover:bg-gray-50/50">
                      <td className="px-6 py-4 font-semibold text-gray-900">
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                            <FaWhatsapp />
                          </div>
                          <div>
                            <p>{num.displayPhoneNumber}</p>
                            {num.current && <span className="text-[10px] text-gray-400 font-normal">Current</span>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">{num.verifiedName || "-"}</td>
                      <td className="px-6 py-4">
                        <StatusBadge
                          text={num.nameStatusLabel || num.nameStatus}
                          variant={num.nameStatus === "APPROVED" ? "success" : "danger"}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge
                          text={num.status}
                          variant={num.status === "CONNECTED" ? "success" : "neutral"}
                        />
                      </td>
                      <td className="px-6 py-4 font-medium text-emerald-600">
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 mr-2" />
                        {num.qualityLabel || num.qualityRating}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Configuration & Preview Grid */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <Card icon={<FiUser />} title="WhatsApp Profile Settings" subtitle={`Phone ID: ${phone?.id || "-"}`}>
                <div className="space-y-3 p-6 pt-3">
                  {/* Photo Edit */}
                  <div>
                    <label className="mb-2 block text-xs font-semibold text-gray-700">Profile Picture</label>
                    <div className="flex items-center gap-4">
                      <div className="h-16 w-16 overflow-hidden rounded-2xl border border-gray-200 bg-gray-50">
                        {profile?.profilePictureUrl ? (
                          <img src={profile.profilePictureUrl} alt="Profile" className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-gray-400">
                            <FiUser className="h-6 w-6" />
                          </div>
                        )}
                      </div>
                      {/* <div>
                        <button type="button" className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 shadow-sm">
                          Change Picture
                        </button>
                        <p className="mt-1 text-[11px] text-gray-400">Square JPG or PNG. 640×640 recommended.</p>
                      </div> */}
                    </div>
                  </div>

                  <hr className="border-gray-100" />

                  {/* Display Name Edit */}
                  <div>
                    <label className="mb-2 block text-xs font-semibold text-gray-700">Display Name</label>
                    <div className="mb-2 flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">{phone?.verifiedName || "-"}</span>
                      <StatusBadge
                        text={phone?.nameStatusLabel || phone?.nameStatus || "-"}
                        variant={phone?.nameStatus === "APPROVED" ? "success" : "danger"}
                      />
                    </div>
                    {/* <div className="flex gap-2">
                      <input
                        type="text"
                        value={newDisplayName}
                        onChange={(e) => setNewDisplayName(e.target.value)}
                        placeholder="Enter new display name"
                        className="h-9 flex-1 rounded-lg border border-gray-200 px-3 text-xs outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                      />
                      <button type="button" className="h-9 rounded-lg bg-gray-900 px-4 text-xs font-semibold text-white transition hover:bg-gray-800">
                        Submit Review
                      </button>
                    </div> */}
                  </div>

                  <div className="rounded-xl bg-gray-50 p-4 border border-gray-100 flex gap-3 items-start">
                    <FiShield className="mt-0.5 text-emerald-600 shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-gray-900">Official Business Account</p>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {phone?.officialBusinessAccount
                          ? "Your account is verified as an official business account."
                          : "This account is not currently marked as an official business account."}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            <WhatsAppPreview
              phone={phone}
              profile={profile}
              about={about}
              categoryLabel={categoryLabel}
              website={website1}
            />
          </div>

          {/* Modal */}
          {showBusinessForm && (
            <BusinessProfileModal
              categories={categories}
              category={category}
              setCategory={setCategory}
              about={about}
              setAbout={setAbout}
              description={description}
              setDescription={setDescription}
              address={address}
              setAddress={setAddress}
              email={email}
              setEmail={setEmail}
              website1={website1}
              setWebsite1={setWebsite1}
              website2={website2}
              setWebsite2={setWebsite2}
              onClose={() => setShowBusinessForm(false)}
            />
          )}
        </div>
      )}
    </section>
  );
}


function Card({ icon, title, subtitle, children }: { icon: React.ReactNode; title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
      <div className="flex items-center gap-3 border-b border-gray-100 px-6 py-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-50 text-emerald-600">
          {icon}
        </div>
        <div>
          <h2 className="text-sm font-bold text-gray-900">{title}</h2>
          {subtitle && <p className="text-[11px] text-gray-400">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function StatCard({ icon, label, value, active }: { icon: React.ReactNode; label: string; value: string; active?: boolean }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition hover:-translate-y-0.5">
      <div className="flex items-center justify-between text-gray-400">
        <span className="text-[10px] font-bold uppercase tracking-wider">{label}</span>
        <div className={`p-1.5 rounded-lg ${active ? "bg-emerald-50 text-emerald-600" : "bg-gray-50 text-gray-500"}`}>
          {icon}
        </div>
      </div>
      <p className={`mt-2 text-sm font-bold ${active ? "text-emerald-600" : "text-gray-900"}`}>{value}</p>
    </div>
  );
}

function StatusBadge({ text, variant }: { text: string; variant: "success" | "danger" | "neutral" }) {
  const styles = {
    success: "bg-emerald-50 text-emerald-700 dot-emerald-500",
    danger: "bg-red-50 text-red-700 dot-red-500",
    neutral: "bg-gray-100 text-gray-600 dot-gray-400",
  };

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${styles[variant].split(" ")[0]} ${styles[variant].split(" ")[1]}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {text}
    </span>
  );
}

function InfoItem({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{label}</p>
      <p className="mt-1 text-xs font-semibold text-gray-800 break-all">{value || "-"}</p>
    </div>
  );
}

function WhatsAppPreview({ phone, profile, about, categoryLabel, website }: any) {
  return (
    <div className="rounded-2xl bg-[radial-gradient(120%_140%_at_0%_0%,#0288D1_0%,#014B73_45%,#0E1C2B_100%)] p-6 text-white shadow-xl h-fit">
      <div className="text-center">
        <div className="mx-auto h-20 w-20 overflow-hidden rounded-full border-2 border-white/10 bg-gray-800">
          {profile?.profilePictureUrl ? (
            <img src={profile.profilePictureUrl} alt="Preview" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xl font-bold text-gray-400">
              {phone?.verifiedName?.[0] || "B"}
            </div>
          )}
        </div>
        <div className="mt-3 flex items-center justify-center gap-1">
          <h3 className="font-bold text-sm">{phone?.verifiedName || "Business"}</h3>
          {phone?.nameStatus === "APPROVED" && <FiCheckCircle className="text-[#25D366]" />}
        </div>
        <p className="text-[11px] text-gray-400 mt-0.5">{phone?.displayPhoneNumber || "+00 000 000 0000"}</p>
      </div>

      <div className="mt-6 space-y-3 border-t border-white/10 pt-4 text-xs">
        <div>
          <span className="text-[9px] uppercase font-semibold text-gray-500 block">About</span>
          <p className="text-gray-300 mt-0.5">{about || "Hey there! I am using WhatsApp."}</p>
        </div>
        <div>
          <span className="text-[9px] uppercase font-semibold text-gray-500 block">Category</span>
          <p className="text-gray-300 mt-0.5">{categoryLabel}</p>
        </div>
        {website && (
          <div>
            <span className="text-[9px] uppercase font-semibold text-gray-500 block">Website</span>
            <a href={website} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline block truncate mt-0.5">
              {website}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export function CharacterCount({ current, max }: { current: number; max: number }) {
  return <div className="mt-1 text-right text-[10px] text-gray-400">{current}/{max}</div>;
}